import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { getModalSwitch, handleGetRolesData, resetState, setOrderByDirection, setOrderByProperty } from '@app/store/actions';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import {
    getMenuItemById,
    infiniteScrollItemsFetchLimit,
    membersCount,
    hasOrgPermission
} from '@app/helpers';
import { IRole, ITableHead, IListPageBarEnabled, FilterEnum, IActiveFilter, OrderType, Order, TranslationsTypes, IFabMenu } from '@app/models';
import {
    FabMenu,
    IModalData,
    ModalComponent,
    PasslyTable
} from '@app/components';
import { basicService } from '@app/services';
import {ManageSystem, ReadDirectory, ManageAccount, Audit, Administrator} from '@app/store/constants';
import { AddOrganizationRolesDialog } from '@app/pages/directoryManager/dialogs/addOrganizationRoles.dialog';

interface IRolesTableData {
    name: string;
    count: number;
    member: TranslationsTypes;
    lastModified: Date;
    id: string;
}

enum TableHeadEnum {
    name = 'name',
    lastModified = 'lastModified'
}

const RolesPage = () => {
    let rows: IRolesTableData[] = [];
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Roles);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('name');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [noPermsToView, setNoPermsToView] = useState(false);

    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const roles = useSelector((state: any) => state.roles.roles);
    const roleMemberCount = useSelector((state: any) => state.roles.roleMemberCount);
    const rolesTotalCount = useSelector((state: any) => state.roles.count);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: false
    };

    const onNameClick = (e: any) => basicService.redirect('/directory/roles/details/' + e.currentTarget.dataset.id);

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.name,
            label: 'ROLES_NAME',
            type: 'link',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.name,
            onClick: onNameClick
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'ROLES_LAST_MODIFIED',
            type: 'date'
        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        setNoPermsToView(!hasOrgPermission([ManageSystem, ReadDirectory, ManageAccount, Audit]));
    }, [organizationFilter]);

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchData = (skipCount: number, isReset: boolean) => {
        dispatch(handleGetRolesData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, organizationFilter, isReset, activeFilters));
    };

    if (!loadingData) {
        const roleCount = (id: string) => roleMemberCount.filter((role: IRole) => role.Id === id);

        if (roles) {
            rows = roles.map((role: IRole) => {
                let count = 0;
                roleCount(role.Id)[0] !== undefined ? (count = roleCount(role.Id)[0].Count) : (count = 0);
                const data: IRolesTableData = {
                    name: role.Name,
                    count: count,
                    member: membersCount(count),
                    lastModified: role.LastModified,
                    id: role.Id
                };
                return data;
            });
        }
    }

    const addRolesModal = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_ROLES'),
            modalSwitcher: modalSwitchState,
            form: <AddOrganizationRolesDialog handleRefresh={fetchData}/>
        });
    };

    const menu: IFabMenu = {
        name: 'ADD_ROLES',
        actions: [
            {
                name: 'ADD_ROLES',
                method: addRolesModal,
                iconElement: <i className='icon-user-roles' />
            }
        ]
    };

    return (
        <>
            <ModalComponent {...modalData} />

            {noPermsToView ? (
                <div className='table--empty'>{translation('NO_ORG_ROLE_PERMISSIONS')}</div>
            ) : (
                <>
                    <PasslyTable
                        rows={rows}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={rolesTotalCount}
                        onFetch={fetchData}
                        withCheckboxes={false}
                        emptyListHelper={translation('EMPTY_ROLE_LIST')}
                        loadingData={loadingData}
                    />
                </>
            )}
            {!loadingData && hasOrgPermission([Administrator]) && <FabMenu menu={menu} />}
        </>
    );
};

export default RolesPage;
